import { ScrollElementContext } from "@/providers/scroll-element";
import { useContext, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { StickyContextItem, useStickyContext } from "./StickyContext";

export function StickyElement({
  side = "top",
  children,
  renderChildren,
  stickyProps,
  ...props
}: {
  side?: "top" | "bottom";
  children?: React.ReactNode;
  renderChildren?: (isSticky: boolean) => React.ReactNode;
  stickyProps?: React.JSX.IntrinsicElements["div"];
} & React.JSX.IntrinsicElements["div"]) {
  const scrollEl = useContext(ScrollElementContext);
  const stickyContext = useStickyContext();

  if (!scrollEl) {
    throw new Error("StickyElement must be used within a ScrollElementContext");
  }

  const [edgeEl, setEdgeEl] = useState<HTMLDivElement | null>(null);
  const [stickyEl, setStickyEl] = useState<HTMLDivElement | null>(null);
  const [edgeElIsOffScreen, setEdgeElIsOffScreen] = useState(false);
  const [stickyElIsOnScreen, setStickyElIsOnScreen] = useState(false);
  const [height, setHeight] = useState(0);

  const sideItems = useMemo(
    () => stickyContext[`${side}Items`],
    [stickyContext, side],
  );

  const stickyOffset = useMemo(() => {
    let offset = 0;
    for (const item of sideItems) {
      if (item.element === stickyEl) break;
      offset += item.height;
    }
    return offset;
  }, [sideItems, stickyEl]);

  const [edgeIntersectionObserver, stickyIntersectionObserver] = useMemo(() => {
    const edgeIntersectionObserver = new IntersectionObserver(
      ([entry = null]) => {
        if (!entry) return;
        setEdgeElIsOffScreen(!entry.isIntersecting);
      },
      {
        root: scrollEl,
        rootMargin:
          side === "top"
            ? `-${stickyOffset}px 0px 0px 0px`
            : `0px 0px -${stickyOffset}px 0px`,
      },
    );

    const stickyIntersectionObserver = new IntersectionObserver(
      ([entry = null]) => {
        if (!entry) return;
        setStickyElIsOnScreen(entry.isIntersecting);
      },
      {
        root: scrollEl,
        rootMargin:
          side === "top"
            ? `-${stickyOffset}px 0px 0px 0px`
            : `0px 0px -${stickyOffset}px 0px`,
      },
    );

    return [edgeIntersectionObserver, stickyIntersectionObserver];
  }, [scrollEl, stickyOffset, side]);

  useEffect(() => {
    if (!edgeEl) return;
    edgeIntersectionObserver.observe(edgeEl);
    return () => {
      edgeIntersectionObserver.disconnect();
    };
  }, [edgeIntersectionObserver, edgeEl]);

  useEffect(() => {
    if (!stickyEl) return;
    const resizeObserver = new ResizeObserver(([entry = null]) => {
      if (!entry) return;
      const [size = null] = entry.borderBoxSize;
      if (!size) return;
      setHeight(size.blockSize);
    });
    resizeObserver.observe(stickyEl, { box: "border-box" });
    return () => {
      resizeObserver.disconnect();
    };
  }, [stickyEl]);

  useEffect(() => {
    if (!stickyEl) return;
    stickyIntersectionObserver.observe(stickyEl);
    return () => {
      stickyIntersectionObserver.disconnect();
    };
  }, [stickyIntersectionObserver, stickyEl]);

  const isSticky = useMemo(
    () => edgeElIsOffScreen && stickyElIsOnScreen,
    [edgeElIsOffScreen, stickyElIsOnScreen],
  );

  const item = useMemo<StickyContextItem | null>(
    () =>
      stickyEl && isSticky
        ? {
            element: stickyEl,
            height,
            side,
          }
        : null,
    [stickyEl, isSticky, height, side],
  );

  useEffect(() => {
    if (!item?.element) return;
    stickyContext.updateItem(item.element, item);
    return () => {
      stickyContext.updateItem(item.element, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, stickyContext.id]);

  return (
    <>
      {side === "top" && <div ref={setEdgeEl} />}

      <div
        {...props}
        ref={(stickyEl) => {
          setStickyEl(stickyEl);
          if (props.ref) {
            if (typeof props.ref === "function") {
              props.ref(stickyEl);
            } else {
              props.ref.current = stickyEl;
            }
          }
        }}
        {...(isSticky && stickyProps)}
        className={useMemo(
          () =>
            twMerge(
              "sticky z-50",
              props.className,
              isSticky && stickyProps?.className,
            ),
          [props.className, isSticky, stickyProps?.className],
        )}
        style={useMemo(
          () => ({
            [side]: stickyOffset,
            ...props.style,
            ...(isSticky && stickyProps?.style),
          }),
          [side, stickyOffset, props.style, isSticky, stickyProps?.style],
        )}
      >
        {renderChildren ? renderChildren(isSticky) : children}
      </div>

      {side === "bottom" && <div ref={setEdgeEl} />}
    </>
  );
}
